import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Input, InputNumber, Switch, Button, Space, App, Alert, Tag } from 'antd';
import { SaveOutlined, ReloadOutlined, MailOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { createAuthApi } from '@zero/auth-admin-ui';

/**
 * SmtpConfigPage — admin chỉnh SMTP runtime, lưu vào Auth_AppConfig (IsPublic=0).
 * Backend SmtpEmailNotifier đọc DB trực tiếp (cache 60s, invalidate khi save).
 *
 * Password handle cẩn thận:
 *   - GET trả password thật (cần cho admin verify) → backend cần Auth.Config.View
 *   - Field render Input.Password (hide ký tự) — admin click "eye" mới xem
 *   - Empty trong form = giữ nguyên password cũ trong DB (không clear)
 */
const SMTP_KEYS = [
  { key: 'Smtp.Host',        field: 'Host',        type: 'string', label: 'Host',         placeholder: 'smtp.gmail.com' },
  { key: 'Smtp.Port',        field: 'Port',        type: 'number', label: 'Port',         placeholder: '587' },
  { key: 'Smtp.UseSsl',      field: 'UseSsl',      type: 'bool',   label: 'Use SSL',      help: 'true=465 SSL on connect; false=STARTTLS (587/25)' },
  { key: 'Smtp.Username',    field: 'Username',    type: 'string', label: 'Username',     placeholder: 'user@gmail.com' },
  { key: 'Smtp.Password',    field: 'Password',    type: 'password', label: 'Password' },
  { key: 'Smtp.FromAddress', field: 'FromAddress', type: 'string', label: 'From address', placeholder: 'noreply@yourcompany.com' },
  { key: 'Smtp.FromName',    field: 'FromName',    type: 'string', label: 'From name',    placeholder: 'Your Company' },
];

export function SmtpConfigPage({ axios }) {
  const api = useMemo(() => createAuthApi(axios), [axios]);
  const { t } = useTranslation();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving,  setSaving]  = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.listAppConfig();
      const list = r.data?.Data ?? [];
      const lookup = (key) => list.find((e) => e.ConfigKey === key)?.ConfigValue ?? '';
      const values = {};
      for (const k of SMTP_KEYS) {
        const raw = lookup(k.key);
        if (k.type === 'number') values[k.field] = raw === '' ? null : Number(raw);
        else if (k.type === 'bool') values[k.field] = raw === 'true' || raw === '1';
        else values[k.field] = raw;
      }
      form.setFieldsValue(values);
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  const save = async (values) => {
    setSaving(true);
    try {
      for (const k of SMTP_KEYS) {
        let raw = values[k.field];
        if (k.type === 'bool')   raw = raw ? 'true' : 'false';
        if (k.type === 'number') raw = raw == null || raw === '' ? null : String(raw);
        if (raw === '') raw = null;
        await api.upsertAppConfig(k.key, {
          ConfigKey: k.key, ConfigValue: raw, ValueType: k.type === 'bool' ? 'Boolean' : k.type === 'number' ? 'Number' : 'String', IsPublic: false,
        });
      }
      message.success(t('common.savedAt') + ' ' + new Date().toLocaleTimeString());
    } catch (err) {
      message.error(err?.response?.data?.Message ?? err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card size="small" extra={
      <Space size={4}>
        <Button size="small" icon={<ReloadOutlined />} onClick={refresh}>Reload</Button>
      </Space>
    }>
      <Alert
        type="info" showIcon style={{ marginBottom: 12 }}
        message="Cấu hình SMTP server"
        description={
          <span>
            Email được dùng cho luồng <strong>quên mật khẩu</strong>, <strong>thông báo cảnh báo</strong> và
            các email hệ thống khác. Lưu trong DB (Auth_AppConfig) — thay đổi áp dụng tức thì
            không cần restart server. Field bỏ trống → giữ giá trị cũ.
          </span>
        }
      />
      <Form form={form} layout="vertical" onFinish={save} disabled={loading} autoComplete="off">
        <Space wrap style={{ width: '100%' }}>
          <Form.Item label="Host" name="Host" style={{ minWidth: 280 }}>
            <Input placeholder="smtp.gmail.com" prefix={<MailOutlined />} />
          </Form.Item>
          <Form.Item label="Port" name="Port" style={{ minWidth: 120 }}>
            <InputNumber min={1} max={65535} style={{ width: '100%' }} placeholder="587" />
          </Form.Item>
          <Form.Item label="Use SSL" name="UseSsl" valuePropName="checked"
                     extra="bật cho port 465 (SslOnConnect); tắt cho 587 (STARTTLS)">
            <Switch />
          </Form.Item>
        </Space>
        <Form.Item label="Username" name="Username">
          <Input placeholder="user@example.com" autoComplete="off" />
        </Form.Item>
        <Form.Item label="Password" name="Password"
                   extra="Lưu trong DB. App password (Gmail) hoặc API key (SendGrid) khuyến nghị thay password thật.">
          <Input.Password placeholder="••••••" autoComplete="new-password" />
        </Form.Item>
        <Space wrap style={{ width: '100%' }}>
          <Form.Item label="From address" name="FromAddress" style={{ minWidth: 280 }}>
            <Input placeholder="noreply@yourcompany.com" />
          </Form.Item>
          <Form.Item label="From name" name="FromName" style={{ minWidth: 220 }}>
            <Input placeholder="Your Company" />
          </Form.Item>
        </Space>
        <Form.Item style={{ marginBottom: 0 }}>
          <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
            Save SMTP config
          </Button>
        </Form.Item>
      </Form>

      <Alert
        type="warning" showIcon style={{ marginTop: 16 }}
        message="Test gửi mail"
        description={
          <span>
            Sau khi save, dùng luồng "Quên mật khẩu" trên trang đăng nhập để test (nhập email user thật, hệ thống gửi link reset).
            Hoặc xem log API: <Tag>POST /api/auth/forgot-password</Tag> → log "Email sent to {`{email}`}".
          </span>
        }
      />
    </Card>
  );
}
